"use client";

import {
  Alert,
  Box,
  Card,
  Container,
  Group,
  List,
  Paper,
  SimpleGrid,
  Text,
  Title,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";

import { FrameworkBreadcrumb } from "@/app/(marketing)/decision-making-frameworks/components/FrameworkBreadcrumb";
import {
  DECISION_DIFFICULTY_FRAMEWORK_NAME,
  DECISION_MAKING_FRAMEWORKS,
} from "@/app/(marketing)/decision-making-frameworks/constants";

export default function DecisionDifficultyPage() {
  const framework = DECISION_MAKING_FRAMEWORKS[DECISION_DIFFICULTY_FRAMEWORK_NAME];

  return (
    <Container size="md" py="xl">
      <FrameworkBreadcrumb framework={framework} />

      <Title order={1} mb="md">
        {framework.name}
      </Title>
      <Text size="lg" c="dimmed" mb="xl">
        {framework.description}
      </Text>

      <Alert variant="light" color="blue" mb="xl">
        <Text>
          The decision difficulty model helps you see what kind of decision you're making – whether
          it's a no-brainer, a hard choice, or something in between. By understanding the nature of
          your decision, you can apply the right approach and move forward with confidence.
        </Text>
        <Text mt="md">
          <Text span fw={600}>
            Not all decisions are created equal.
          </Text>{" "}
          Some require deep analysis, while others can be made quickly. This framework helps you
          identify which is which.
        </Text>
      </Alert>

      <Title order={2} mb="md">
        How to use the decision difficulty model
      </Title>
      <Text mb="lg">To use this model, consider two key factors of your decision:</Text>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <Paper p="lg" withBorder radius="md">
          <Title order={3} size="h4" mb="sm">
            Impact
          </Title>
          <Text>
            How significant are the consequences of this decision? Will it affect many people,
            involve substantial resources, or have long-lasting effects?
          </Text>
          <Box mt="md">
            <Group>
              <Text fw={500}>Low impact:</Text>
              <Text>Few consequences, easily reversible</Text>
            </Group>
            <Group mt="xs">
              <Text fw={500}>High impact:</Text>
              <Text>Major consequences, difficult to reverse</Text>
            </Group>
          </Box>
        </Paper>

        <Paper p="lg" withBorder radius="md">
          <Title order={3} size="h4" mb="sm">
            Comparability
          </Title>
          <Text>
            How easy is it to compare the available options? Are they similar in nature or very
            different?
          </Text>
          <Box mt="md">
            <Group>
              <Text fw={500}>Easy to compare:</Text>
              <Text>Similar options, clear metrics</Text>
            </Group>
            <Group mt="xs">
              <Text fw={500}>Hard to compare:</Text>
              <Text>Different options, subjective factors</Text>
            </Group>
          </Box>
        </Paper>
      </SimpleGrid>

      <Title order={2} mb="md">
        The four types of decisions
      </Title>
      <Text mb="md">Based on these two factors, decisions fall into four categories:</Text>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <Card withBorder p="lg" radius="md">
          <Title order={3} size="h4" mb="sm">
            No-brainer
          </Title>
          <Group mb="md">
            <Text fw={500}>Impact:</Text>
            <Text>Low</Text>
            <IconCheck size={16} color="green" />
          </Group>
          <Group mb="md">
            <Text fw={500}>Comparability:</Text>
            <Text>Easy</Text>
            <IconCheck size={16} color="green" />
          </Group>
          <Text>
            Move fast and go with your gut feeling. These decisions don't require much analysis.
          </Text>
          <Text mt="xs" size="sm" fs="italic">
            Example: Choosing between two similar vendors for office supplies
          </Text>
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={3} size="h4" mb="sm">
            Apples/Oranges choice
          </Title>
          <Group mb="md">
            <Text fw={500}>Impact:</Text>
            <Text>Low</Text>
            <IconCheck size={16} color="green" />
          </Group>
          <Group mb="md">
            <Text fw={500}>Comparability:</Text>
            <Text>Hard</Text>
            <IconX size={16} color="red" />
          </Group>
          <Text>
            Refine your options based on what is really important to you. Focus on key criteria.
          </Text>
          <Text mt="xs" size="sm" fs="italic">
            Example: Allocating a small discretionary budget between team training or equipment
            upgrades
          </Text>
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={3} size="h4" mb="sm">
            Big choice
          </Title>
          <Group mb="md">
            <Text fw={500}>Impact:</Text>
            <Text>High</Text>
            <IconX size={16} color="red" />
          </Group>
          <Group mb="md">
            <Text fw={500}>Comparability:</Text>
            <Text>Easy</Text>
            <IconCheck size={16} color="green" />
          </Group>
          <Text>
            Gain confidence before making the decision. Verify assumptions and gather data.
          </Text>
          <Text mt="xs" size="sm" fs="italic">
            Example: Selecting between two well-defined strategic initiatives with similar ROI
            projections
          </Text>
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={3} size="h4" mb="sm">
            Hard choice
          </Title>
          <Group mb="md">
            <Text fw={500}>Impact:</Text>
            <Text>High</Text>
            <IconX size={16} color="red" />
          </Group>
          <Group mb="md">
            <Text fw={500}>Comparability:</Text>
            <Text>Hard</Text>
            <IconX size={16} color="red" />
          </Group>
          <Text>
            Use structured tools like a decision matrix to carefully evaluate options based on
            different factors.
          </Text>
          <Text mt="xs" size="sm" fs="italic">
            Example: Deciding between organizational restructuring, geographic expansion, or product
            diversification
          </Text>
        </Card>
      </SimpleGrid>

      <Text mb="xl">
        When you can categorize your decision using this model, it enables you to apply the right
        level of analysis and move forward with appropriate confidence.
      </Text>

      <Title order={2} mb="md">
        Business application example
      </Title>
      <Text mb="md">
        Let's look at how a product manager might use this framework when deciding on the next
        quarter's development priorities:
      </Text>

      <Paper p="lg" withBorder radius="md" mb="xl">
        <Title order={3} size="h4" mb="sm">
          Scenario: Product Roadmap Decision
        </Title>
        <Text mb="md">A product manager needs to decide between three options:</Text>
        <List withPadding mb="md">
          <List.Item>Option A: Improve existing features based on customer feedback</List.Item>
          <List.Item>Option B: Develop a new integration with a popular enterprise tool</List.Item>
          <List.Item>Option C: Redesign the user interface to improve usability</List.Item>
        </List>

        <Text fw={500} mb="xs">
          Analysis:
        </Text>
        <Text mb="xs">This is a "Hard choice" because:</Text>
        <List withPadding mb="md">
          <List.Item>
            <Text fw={500}>High impact:</Text> The decision will affect product direction, resource
            allocation, and customer satisfaction
          </List.Item>
          <List.Item>
            <Text fw={500}>Hard to compare:</Text> Each option addresses different needs (customer
            satisfaction, market expansion, user experience)
          </List.Item>
        </List>

        <Text fw={500} mb="xs">
          Approach:
        </Text>
        <Text>
          The product manager should use a structured decision-making tool like a weighted decision
          matrix to evaluate each option against criteria such as revenue potential, development
          effort, strategic alignment, and customer impact. They should also gather input from
          stakeholders across the organization before making the final decision.
        </Text>
      </Paper>
    </Container>
  );
}
